import type {
  AdminUser,
  ApiRole,
  GuardianRelationship,
  ImportUsersResult,
  School,
  UserSchoolAccess,
} from "../../lib/api";
import { translations, type Locale } from "../../lib/i18n";

export function userRolesFor(user: Pick<AdminUser, "role" | "roles">) {
  return user.roles?.length ? user.roles : [user.role];
}

export function userRolesForSchool(user: Pick<AdminUser, "role" | "roles" | "schoolRoles">, schoolId: string) {
  const schoolRoles = user.schoolRoles?.filter((item) => item.schoolId === schoolId).map((item) => item.role) || [];
  return schoolRoles.length ? [...new Set(schoolRoles)] : userRolesFor(user);
}

export function schoolAccessRowsFor(user: Pick<AdminUser, "schoolRoles">, schools: School[]): UserSchoolAccess[] {
  const schoolById = new Map(schools.map((school) => [school.id, school]));
  const rolesBySchool = new Map<string, ApiRole[]>();
  user.schoolRoles?.forEach((item) => rolesBySchool.set(item.schoolId, [...(rolesBySchool.get(item.schoolId) || []), item.role]));
  return [...rolesBySchool.entries()]
    .map(([schoolId, roles]) => {
      const school = schoolById.get(schoolId);
      return school ? { school, roles: [...new Set(roles)] } : null;
    })
    .filter((item): item is UserSchoolAccess => Boolean(item))
    .sort((a, b) => a.school.name.localeCompare(b.school.name, "sq", { sensitivity: "base" }));
}

export function userDisplayName(user: Pick<AdminUser, "firstName" | "lastName" | "name" | "email">) {
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.name || user.email;
}

export function userSearchText(user: AdminUser) {
  return `${userDisplayName(user)} ${user.email}`.toLowerCase();
}

export function relationshipLabel(relationship: GuardianRelationship, t: typeof translations.sq | typeof translations.en) {
  if (relationship === "father") return t.relationshipFather;
  if (relationship === "mother") return t.relationshipMother;
  return t.relationshipGuardian;
}

export function formatDateTime(value: string, locale: Locale) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale === "sq" ? "sq-XK" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDate(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

export function compareUsersByName(a: AdminUser, b: AdminUser) {
  return userDisplayName(a).localeCompare(userDisplayName(b), "sq", { sensitivity: "base" });
}

export function resultLine(result: ImportUsersResult, t: typeof translations.sq | typeof translations.en) {
  return `${result.created} ${t.createdShort}, ${result.updated} ${t.updatedShort}, ${result.linked} ${t.linkedShort}, ${result.pending || 0} ${t.pendingShort}, ${result.staged || 0} ${t.stagedShort}, ${result.invited} ${t.invitedShort}, ${result.skipped} ${t.skippedShort}`;
}

export function rowsToCsv(rows: unknown[][]) {
  return rows
    .filter((row) => row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== ""))
    .map((row) => row.map((cell) => csvCellForImport(cell)).join(";"))
    .join("\n");
}

export function csvCellForImport(value: unknown) {
  const text = value instanceof Date ? value.toISOString() : String(value ?? "");
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
